import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router';
import { replace } from 'redux-first-history';
import { getIDFromUrl } from '@rossum/api-client';
import { Queue } from '@rossum/api-client/queues';
import { Stack } from '@rossum/ui/material';
import { isEmbedded } from '../../constants/config';
import Loader from '../../containers/Loader';
import { assertNever } from '../../lib/typeUtils';
import {
  constructDocumentUrl,
  getCurrentAnnotationId,
  getIDFromString,
} from '../../lib/url';
import {
  clearAnnotationData,
  nextAnnotableAnnotation,
} from '../../redux/modules/annotation/actions';
import {
  annotationSelector,
  annotationSideloadsSelector,
} from '../../redux/modules/annotation/selectors';
import { confirmEditModeFulfilled } from '../../redux/modules/editMode/actions';
import { throwError } from '../../redux/modules/messages/actions';
import { safeOrganizationSelector } from '../../redux/modules/organization/selectors';
import { getAnnotationBacklink } from '../../routes/useAnnotationBacklink';
import { Document } from '../../types/document';
import {
  areSuggestedSplitsActive,
  CancelChangesDialog,
  EditDocumentConfig,
  extractBackendErrorMessage,
  hasChangesToConfirm,
  humanizePageIdsInMessage,
  useEditState,
} from '../document-edit-core';
import { notify } from '../toaster';
import { EditDocumentHeader } from './EditDocumentHeader';
import { EditDocumentSplitPane } from './EditDocumentSplitPane';
import { EditDocumentTopBar } from './EditDocumentTopBar';
import { EditedPartList } from './EditedPartList';
import { OriginalDocument } from './OriginalDocument';
import { useUiState } from './uiState';
import { useEditMode } from './useEditMode';
import { useLeaveEditMode } from './useLeaveEditMode';

export const EditDocument = () => {
  const annotation = useSelector(annotationSelector);
  const annotationSideloads = useSelector(annotationSideloadsSelector);
  const organization = useSelector(safeOrganizationSelector);

  const location = useLocation<{ backlink?: string }>();

  const { config, confirm, confirmInProgress } = useEditMode({
    annotation,
    annotationSideloads,
    organization,
    // Read lazily: `useCancelEditMode` runs during unmount, once the URL has
    // already changed to the redirect destination.
    getTargetAnnotationId: () =>
      getCurrentAnnotationId(window.location.pathname),
  });

  const leaveEditMode = useLeaveEditMode({
    annotationUrl: annotation?.url,
    currentAnnotationId: getCurrentAnnotationId(location.pathname),
    getDestination: () =>
      isEmbedded()
        ? constructDocumentUrl({
            id: getCurrentAnnotationId(location.pathname),
          })
        : (location.state?.backlink ?? getAnnotationBacklink()),
  });

  const [cancelChangesDialogOpen, setCancelChangesDialogOpen] = useState(false);
  const [shouldAskForConfirm, setShouldAskForConfirm] = useState(false);

  const handleLeaveEditMode = () => {
    if (shouldAskForConfirm) {
      setCancelChangesDialogOpen(true);
    } else {
      leaveEditMode();
    }
  };

  return (
    <Stack sx={{ height: '100%' }}>
      <EditDocumentTopBar
        documentName={annotationSideloads.document?.originalFileName}
        onLeaveEditMode={handleLeaveEditMode}
      />
      {config ? (
        <EditDocumentContent
          config={config}
          confirm={confirm}
          confirmInProgress={confirmInProgress}
          currentQueue={annotationSideloads.queue}
          document={annotationSideloads.document}
          onChangesToConfirmChange={setShouldAskForConfirm}
        />
      ) : (
        <Loader />
      )}
      <CancelChangesDialog
        open={cancelChangesDialogOpen}
        onClose={() => setCancelChangesDialogOpen(false)}
        onCancel={leaveEditMode}
      />
    </Stack>
  );
};

const EditDocumentContent = ({
  config,
  confirm,
  confirmInProgress,
  currentQueue,
  document,
  onChangesToConfirmChange,
}: {
  config: EditDocumentConfig;
  confirm: ReturnType<typeof useEditMode>['confirm'];
  confirmInProgress: boolean;
  currentQueue: Queue | undefined;
  document: Document | undefined;
  onChangesToConfirmChange: (hasChanges: boolean) => void;
}) => {
  const dispatch = useDispatch();

  const defaultListLocation = getAnnotationBacklink();

  const [editState, dispatchEdit] = useEditState(config);

  const [uiState, setUiState] = useUiState(confirmInProgress);

  const suggestedSplitsActive = areSuggestedSplitsActive(
    editState.parts,
    editState.config
  );

  const canUndo =
    !uiState.cancelChangesDialogOpen &&
    !uiState.confirmInProgress &&
    !uiState.draggedPage;

  const hasChanges = useMemo(() => {
    return hasChangesToConfirm(editState);
  }, [editState]);

  const hasRequiredEditValuesFilledOut = useMemo(() => {
    const queueEditValues = currentQueue?.settings.uiEditValues ?? [];
    const requiredEditValueIds = queueEditValues
      .filter(ev => ev.required)
      .map(ev => ev.id);

    if (requiredEditValueIds.length === 0) return true;

    return editState.parts.every(part => {
      if (part.annotationType === 'inaccessible') return true;
      return requiredEditValueIds.every(
        id => part.editValues[id] && part.editValues[id].trim() !== ''
      );
    });
  }, [editState.parts, currentQueue?.settings.uiEditValues]);

  // Ugly, but works for now
  useEffect(() => {
    onChangesToConfirmChange(hasChanges);
  }, [hasChanges, onChangesToConfirmChange]);

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (canUndo && (e.metaKey || e.ctrlKey) && e.key === 'z') {
        dispatchEdit({ type: 'UNDO' });
        e.preventDefault();
      }

      // WORKAROUND: It seems it's somehow possible to have draggedPage not null
      // when the user is not dragging. It shouldn't happen because both onDragCancel and onDragEnd
      // are resetting the uiState accordingly,
      // Since this situation prevents the user from working,
      // at least we'll provide a way to reset the state.
      if (e.key === 'Escape') {
        setUiState(s => ({ ...s, draggedPage: null }));
      }
    };

    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [dispatchEdit, setUiState, canUndo]);

  return (
    <Stack
      sx={{
        flex: '1 0',
        backgroundColor: theme => theme.palette.background.default,
        px: 4,
        pt: 4,
      }}
      spacing={4}
    >
      <EditDocumentHeader
        hasChanges={hasChanges}
        canConfirm={hasRequiredEditValuesFilledOut}
        document={document}
        editState={editState}
        suggestedSplitsActive={suggestedSplitsActive}
        dispatchEdit={dispatchEdit}
        confirmInProgress={uiState.confirmInProgress}
        onConfirm={() =>
          confirm(editState, {
            onSuccess: response => {
              // This can happen if you delete all pages
              if (!isEmbedded() && response.results.length === 0) {
                dispatch(replace(defaultListLocation));
                return;
              }

              const firstAnnotation = response.results[0]?.annotation;

              const firstAnnotationId = getIDFromString(firstAnnotation);

              const confirmAction =
                currentQueue?.settings.uiOnEditConfirm ?? 'validate_first';

              switch (confirmAction) {
                case 'annotation_list':
                  dispatch(replace(defaultListLocation));
                  break;
                case 'edit_next':
                  // clear annotation data and call next annotable annotation and make it go to /edit
                  dispatch(clearAnnotationData());
                  dispatch(nextAnnotableAnnotation(true));
                  break;
                case 'validate_first':
                  // start annotation the first split annotation
                  dispatch(
                    replace(constructDocumentUrl({ id: firstAnnotationId }))
                  );
                  break;
                default:
                  assertNever(confirmAction);
                  break;
              }

              // This action handles redirect to the first annotation or to return_url for embedded mode
              dispatch(
                confirmEditModeFulfilled(
                  response,
                  getIDFromUrl(editState.config.annotationUrl)
                )
              );
            },
            onError: error => {
              const backendMessage = extractBackendErrorMessage(error);
              if (backendMessage) {
                notify.error({
                  title: humanizePageIdsInMessage(backendMessage, editState),
                });
              } else {
                dispatch(throwError('clientError'));
              }
            },
          })
        }
        onCancel={() => {
          dispatchEdit({
            type: 'RESET',
            newState: {
              parts: config.initialParts,
              config,
            },
          });
        }}
      />
      <EditDocumentSplitPane>
        <OriginalDocument
          editState={editState}
          uiState={[uiState, setUiState]}
          config={config}
        />
        <EditedPartList
          editState={editState}
          dispatchEdit={dispatchEdit}
          uiState={[uiState, setUiState]}
          config={config}
          suggestedSplitsActive={suggestedSplitsActive}
        />
      </EditDocumentSplitPane>
    </Stack>
  );
};
